import { Button } from 'components/Button'
import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { banStatusStrings, commonStrings } from 'constants/strings'
import {
  getStatusLabel,
  getWebhookStatusLabel,
  getWebhookStatusStyle,
} from 'pages/Line/lineAccountView'
import type { TBanStatusChannel } from 'types'
import { formatDateTime } from 'utils'
import { BanStatusLabel } from './BanStatusLabel'
import {
  canEditChannel,
  formatFriends,
  getCategoryLabel,
  getChannelCategory,
} from './banStatusView'

type PausedChannelsTableProps = {
  channels: TBanStatusChannel[]
  loading: boolean
  emptyMessage?: string
  currentOrganizationId?: number
  onEdit?: (lineChannelId: number) => void
}

const headers = banStatusStrings.channels.headers
const COLUMN_COUNT = 9

export function PausedChannelsTable({
  channels,
  loading,
  emptyMessage = banStatusStrings.channels.empty,
  currentOrganizationId,
  onEdit,
}: PausedChannelsTableProps) {
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = (
      <DataTableMessageRow
        colSpan={COLUMN_COUNT}
        message={commonStrings.loading}
      />
    )
  } else if (channels.length === 0) {
    body = <DataTableMessageRow colSpan={COLUMN_COUNT} message={emptyMessage} />
  } else {
    body = channels.map((channel) => {
      const category = getChannelCategory(channel)
      // BAN疑いはひと目で見分けられるようにする（カード表示と揃える）
      const suspected = category === 'autoPaused'
      const rowBackground = suspected ? 'bg-danger-soft' : 'bg-white'
      return (
        <tr
          key={channel.lineChannelId}
          className={`border-t border-border/60 ${rowBackground}`}
        >
          <td
            className={`px-2 py-3 text-left sticky left-0 z-10 ${rowBackground} shadow-[2px_0_4px_rgba(0,0,0,0.05)]`}
          >
            {onEdit && canEditChannel(channel, currentOrganizationId) ? (
              <Button
                type="button"
                size="sm"
                onClick={() => onEdit(channel.lineChannelId)}
              >
                {commonStrings.actions.edit}
              </Button>
            ) : (
              <span className="text-[11px] text-muted">-</span>
            )}
          </td>
          <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
            {channel.organizationName}
          </td>
          <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
            {channel.basicId || banStatusStrings.channels.none}
          </td>
          <td className="max-w-[220px] truncate px-2 py-3 text-[11px] text-ink sm:text-sm">
            {channel.memo || banStatusStrings.channels.none}
          </td>
          <td className="px-2 py-3 text-[11px] sm:text-sm">
            <span
              className={
                suspected ? 'font-semibold text-danger' : 'text-ink'
              }
            >
              {getStatusLabel(channel.status)}
            </span>
          </td>
          <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
            <BanStatusLabel label={getCategoryLabel(category)} help={category} />
          </td>
          <td className="px-2 py-3 text-[11px] sm:text-sm">
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${getWebhookStatusStyle(channel.webhookStatus)}`}
            >
              {getWebhookStatusLabel(channel.webhookStatus)}
            </span>
          </td>
          <td className="px-2 py-3 text-right text-[11px] tabular-nums text-ink sm:text-sm">
            {formatFriends(channel.activeFriends, channel.friendLimit)}
          </td>
          <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
            {formatDateTime(channel.updatedAt)}
          </td>
        </tr>
      )
    })
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1160px]"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight sticky left-0 z-10 bg-surface/70">
            {commonStrings.actions.edit}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {headers.organization}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {headers.basicId}
          </th>
          <th className="px-2 py-3 text-left leading-tight">{headers.memo}</th>
          <th className="px-2 py-3 text-left leading-tight">{headers.status}</th>
          <th className="px-2 py-3 text-left leading-tight">
            {headers.category}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            <BanStatusLabel label={headers.webhookStatus} help="webhook" />
          </th>
          <th className="px-2 py-3 text-right leading-tight">
            {headers.friends}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            <BanStatusLabel label={headers.updatedAt} help="updatedAt" />
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}
